import {
  Component,
  OnDestroy,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MatLegacyAutocomplete as MatAutocomplete,
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
} from '@angular/material/legacy-autocomplete';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  skip,
  startWith,
  takeUntil,
} from 'rxjs/operators';

import { trackById } from '../../../shared/util/track-by.util';
import { IWarehouse } from '../../interfaces/location-warehouse.interface';
import { LocationApiService } from '../../services/api/location/location-api.service';

@Component({
  selector: 'lum-warehouse-search-dropdown',
  templateUrl: './warehouse-search-dropdown.component.html',
  styleUrls: ['./warehouse-search-dropdown.component.scss'],
})
export class WarehouseSearchDropdownComponent implements OnInit, OnDestroy {
  @ViewChild(MatAutocomplete) autocomplete :MatAutocomplete;
  @ViewChild('searchInput') searchInput :ElementRef;

  @Input()
  inputClass :string = 'w-100 lum-input';

  @Input()
  rfqId :number;

  @Input()
  warehouseId :number = 0;

  @Input()
  selected :IWarehouse;

  @Input()
  disabled :boolean = false;

  @Input()
  clearable :boolean = false;

  @Output()
  selectItemEvent :EventEmitter<IWarehouse | null> = new EventEmitter();

  searchControl = new UntypedFormControl();
  searchTerm :string = '';
  selectedItem :IWarehouse | null = null;
  pagination :{ currentPage :number; lastPage :number } | null = null;
  records :IWarehouse[] = [];

  trackById = trackById;

  private readonly _loadingRecords = new BehaviorSubject<boolean>(<boolean>false);
  loadingRecords$ = this._loadingRecords.asObservable();

  private onDestroy$ = new Subject();

  constructor(
    private locationApiService :LocationApiService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.initSelectedItem();
    this.initSearchControlListener();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges({ selected, warehouseId, disabled } :SimpleChanges) :void {
    if (warehouseId) {
      this.warehouseId = warehouseId.currentValue;

      if (this.warehouseId !== warehouseId.previousValue) {
        this.selectedItem = null;
        this.pagination = null;
        this.records = [];
      }
    }

    if (selected) {
      this.selectedItem = selected.currentValue;
    }

    if (disabled) {
      if (disabled.currentValue) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    }

    this.cd.detectChanges();
  }

  displaySelectedItem() :string {
    return (
      this.selectedItem?.location_name || this.selectedItem?.locationName || ''
    );
  }

  onRemoveItem() :void {
    this.selectedItem = null;
    this.selectItemEvent.emit(this.selectedItem);
  }

  onSelectItem(event :MatAutocompleteSelectedEvent) :void {
    const currentSelectedItem = event.option.value;
    if (currentSelectedItem.id === this.selectedItem?.id) {
      return;
    }

    this.selectedItem = currentSelectedItem;
    this.selectItemEvent.emit(this.selectedItem);
  }

  onClosePanel() :void {
    this.searchControl.setValue(
      this.selectedItem?.location_name || this.selectedItem?.locationName || '',
      { emitEvent: false },
    );
    this.records = [];
    this.searchTerm = '';
    this.pagination = null;
    this.searchInput.nativeElement.blur();
  }

  getRecords() :void {
    if (
      (this.pagination &&
        this.pagination.currentPage === this.pagination.lastPage) ||
      this.warehouseId === null
    ) {
      return;
    }

    this._loadingRecords.next(true);
    this.locationApiService
      .get({
        per_page: '20',
        parent_id: this.warehouseId.toString(),
        page: this.pagination ? (this.pagination.currentPage + 1).toString() : '1',
        keyword: this.searchTerm,
      })
      .pipe(
        debounceTime(1000),
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingRecords.next(false);
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.pagination = {
            currentPage: response.current_page,
            lastPage: response.last_page,
          };
          this.records = [...this.records, ...response.data];
        },
        error: (error :any) => {},
      });
  }

  private initSearchControlListener() :void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        startWith([]),
        skip(1),
        map((keyword :any) => {
          if (typeof keyword !== 'string') {
            return null;
          }

          this.records = [];
          this.pagination = null;

          return keyword;
        }),
        filter((keyword :any) => keyword !== null),
        takeUntil(this.onDestroy$),
      )
      .subscribe((keyword :string) => {
        this.searchTerm = keyword;
        this.getRecords();
      });
  }

  private initSelectedItem() :void {
    if (this.selected) {
      this.selectedItem = this.selected;
    }
  }
}
